/**
 * Normalização agressiva de nomes de entidades para agrupamento
 * Remove termos de unidade, filial, shopping, etc.
 * Útil para detectar se entidades são da mesma rede
 */
export function normalizeEntityName(name: string): string {
  if (!name) return '';
  
  let normalized = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^\w\s-]/g, '') // Remove pontuação
    .replace(/\s+/g, ' ')
    .trim();
  
  // Remove termos comuns de unidade/localização
  const termsToRemove = [
    /\b(unidade|un\.?)\s*\d+/gi,
    /\b(loja|filial|shopping|matriz|centro|bairro)\s*\d*/gi,
    /\b(norte|sul|leste|oeste|i|ii|iii|iv|v|vi|vii|viii|ix|x)\b/gi,
    /\b\d+\b/g, // Remove números soltos
    /\s*-\s*/g, // Remove hífens
  ];
  
  termsToRemove.forEach(pattern => {
    normalized = normalized.replace(pattern, ' ');
  });
  
  return normalized.replace(/\s+/g, ' ').trim();
}

/**
 * Normalização preservando informações de unidade
 * Remove apenas acentos e pontuação, mantém números e termos de unidade
 * Útil para diferenciar unidades de uma mesma rede
 */
export function normalizeEntityNamePreservingUnits(name: string): string {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^\w\s-]/g, ' ') // Remove pontuação mas preserva palavras e números
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Compara dois nomes de forma fuzzy (ignora acentos, case, pontuação, espaços)
 * Útil para encontrar entidades mesmo com pequenas variações no nome
 */
export function areNamesEquivalent(name1: string, name2: string): boolean {
  if (!name1 || !name2) return false;
  
  const normalize = (str: string) => str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^\w\s]/g, '') // Remove pontuação
    .replace(/\s+/g, ' ')
    .trim();
  
  return normalize(name1) === normalize(name2);
}

/**
 * Agrupa entidades por nome normalizado (independente de estado/cidade)
 * Útil para detectar redes nacionais
 */
export function groupEntitiesByNormalizedName<T extends { id: number; name: string; state: string }>(
  entities: T[]
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  
  entities.forEach(entity => {
    const normalized = normalizeEntityName(entity.name);
    
    if (!groups.has(normalized)) {
      groups.set(normalized, []);
    }
    groups.get(normalized)!.push(entity);
  });
  
  return groups;
}

/**
 * Agrupa entidades por nome (preservando unidades) + estado + cidade
 * Útil para detectar duplicatas reais (mesmo local, mesma unidade)
 */
export function groupEntitiesByNameStateCity<T extends { id: number; name: string; state: string; city?: string }>(
  entities: T[]
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  
  entities.forEach(entity => {
    const normalized = normalizeEntityNamePreservingUnits(entity.name);
    // Cria chave única combinando nome + estado + cidade
    const key = `${normalized}|${entity.state}|${entity.city || 'N/A'}`;
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(entity);
  });
  
  return groups;
}

/**
 * Tipo para identificar o tipo de agrupamento de duplicatas
 */
export type DuplicateGroupType = 'real-duplicate' | 'network-unit';

/**
 * Identifica se um grupo é uma duplicata real ou unidades de uma rede
 */
export function identifyGroupType<T extends { id: number; name: string; state: string; city?: string }>(
  group: T[]
): DuplicateGroupType {
  if (group.length < 2) return 'real-duplicate';
  
  // Se todos têm o mesmo nome normalizado COM unidade + mesmo estado + mesma cidade = duplicata real
  const firstNormalized = normalizeEntityNamePreservingUnits(group[0].name);
  const firstState = group[0].state;
  const firstCity = group[0].city;
  
  const allSame = group.every(entity => 
    normalizeEntityNamePreservingUnits(entity.name) === firstNormalized &&
    entity.state === firstState &&
    entity.city === firstCity
  );
  
  return allSame ? 'real-duplicate' : 'network-unit';
}
